import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import {
    ActionFunction,
    redirect,
    useNavigate,
    useParams
} from "react-router";
import { useFetcher } from "react-router-dom";
import {
    addLogIgnoreById,
    updateLogIgnoreById
} from "../../shared/client";
import { LogIgnore } from "../../shared/log";
import { Alert } from "reactstrap";

interface ValidationErrors {
    common?:string;
    keyword?: string;
    comment?: string;
}

export const action: ActionFunction = async ({ request, params }) => {
    const module = params["module"] as string;
    const id = params["id"] as string | undefined;
    const formData = await request.formData();

    //const updates = Object.fromEntries(formData) as unknown as LogIgnore;
    //console.log(updates);

    const keyword = formData.get("keyword")?.toString();
    const comment = formData.get("comment")?.toString();
    const active = formData.get("active") === "on" ? true : false;
    const intent = formData.get("intent");
    //console.log(keyword, comment, active, intent);


    // Validate form data
    const errors: ValidationErrors = {};

    if (!keyword || keyword.length > 250) {
        errors.keyword = 'keyword is required and max length should be under 250 characters.';
    }

    if (!comment || comment.length > 1000) {
        errors.comment = 'comment is required and max length should be under 1000 characters.';
    }

    // If there are errors, return them
    if (Object.keys(errors).length > 0) return { errors };

    let logIgnore: LogIgnore;
    let response: LogIgnore|string|undefined;    

    if (intent === "update") {
        response = (await updateLogIgnoreById(module,id ?? "",keyword?.trim() ?? "",comment?.trim(),active));        
    } else {
        response = (await addLogIgnoreById(module,keyword,comment,active)) as LogIgnore;        
    }

    if (typeof response !== 'string' && typeof response !== 'undefined') {
        logIgnore = response as LogIgnore
    } else if(typeof response === 'string') {
        errors.comment = response;
        return { errors }
    } else {
        errors.common = "something went wrong at server !!!";
        return { errors }
    }
   
    console.log(logIgnore);
    if (logIgnore && logIgnore.id?.length > 5)
    {
        if (intent !== "update") {
            return redirect(`/${module}/log/ignore-keyword/update/${logIgnore.id}`);
        }

        return { success: true, log: logIgnore, errors: errors};
    }
     
    return { success: false, errors: errors};
};

interface LogIgnoreFormProps {
    isDisplayOnly: boolean;
    initialData?: LogIgnore; // Pass the initial LogIgnore data when editing
    onSave: (data: LogIgnore) => void;
}

const LogIgnoreFormInternal: React.FC<LogIgnoreFormProps> = ({
    isDisplayOnly,
    initialData,
    onSave,
}) => {
    const { module } = useParams();
    const navigate = useNavigate();
    const [logIgnoreData, setLogIgnoreData] = useState<LogIgnore>(
        initialData || {
            id: "",
            keyword: "",
            comment: "",
            active: false,
            createdAt: DateTime.now(),
            updatedAt: undefined,
        }
    );
    const [isEditMode, setIsEditMode] = useState(false);
    const [addOrUpdateText, setAddOrUpdateText] = useState("Save");
    const fetcher = useFetcher();
    const errors = fetcher.data?.errors as ValidationErrors;
    const success = fetcher.data?.success as boolean;
   
    // State to manage local errors (cleared on user input)
    const [localErrors, setLocalErrors] = useState<ValidationErrors>({});

    // Update local errors when fetcher errors change
    useEffect(() => {
        if (errors) {
        setLocalErrors(errors);
        }
    }, [errors]);    

    // Handle form input changes
    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        checked?: boolean
    ) => {
        const { name, value, type } = event.target;
        console.log(name, value, type);

        if (type === "checkbox") {
            setLogIgnoreData({ ...logIgnoreData, [name]: checked });
        } else {
            setLogIgnoreData({ ...logIgnoreData, [name]: value });
        }

        
        // Clear the error for the current field
        // if (localErrors[name as keyof typeof localErrors]) 

        setLocalErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    // Trigger onSave when the submission is successful
    useEffect(() => {
        if (fetcher.state === 'idle' && fetcher.data?.success) {
            onSave(fetcher.data);
        }
    }, [fetcher.state, fetcher.data, onSave]);
    
    useEffect(() => {
        if (initialData) {
            setLogIgnoreData(initialData);
        }

        let notValidID = !initialData || initialData?.id === undefined || initialData.id === "";

        setIsEditMode(!notValidID);
        setAddOrUpdateText(notValidID ? "Save" : "Update");
    }, [initialData]);

    return (
        <div className="container">
            {isDisplayOnly ? ( "" ) : isEditMode ? ( <h1>Log Ignore Update</h1> ) : ( <h1>Add New Log Ignore</h1> )}
            
            <div className="clearfix p-2"></div>
            {errors?.common && <Alert color="danger">{errors?.common}</Alert>}
            {success && <Alert color="success">Successfully Saved.</Alert>}
            
            <div className="clearfix"></div>
            <fetcher.Form id="log-ignore-form" method="post">
                <div className="mb-3">
                    <label htmlFor="keyword" className="form-label">Keyword</label>
                    <input
                        type="text"
                        className="form-control"
                        id="keyword"
                        name="keyword"
                        value={logIgnoreData.keyword}
                        onChange={handleInputChange}
                    />
                    {localErrors?.keyword && <p style={{ color: 'red' }}>{localErrors.keyword}</p>}
                </div>
                <div className="mb-3">
                    <label htmlFor="comment" className="form-label">Comment</label>
                    <textarea
                        className="form-control"
                        id="comment"
                        name="comment"
                        value={logIgnoreData.comment}
                        onChange={handleInputChange}
                    />
                    {localErrors?.comment && <p style={{ color: 'red' }}>{localErrors.comment}</p>}
                </div>
                <div className="mb-3 form-check">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id="active"
                        name="active"
                        checked={logIgnoreData.active}
                        onChange={(e) => handleInputChange(e, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="active">Active</label>
                </div>
                <button
                    type="submit"
                    className="btn btn-primary"
                    name="intent"
                    disabled={fetcher.state === 'submitting'}
                    value={addOrUpdateText.toLowerCase()}
                >
                    {addOrUpdateText}
                </button>
                &nbsp;
                <button
                    type="button"
                    className="btn btn-primary"
                    name="cancel"
                    disabled={fetcher.state === 'submitting'}
                    value="back_to_list"
                    onClick={(e) => {
                        navigate(`/${module}/log/ignore-keyword/list`);
                    }}
                >
                    Back To List
                </button>

                
            </fetcher.Form>
        </div>
    );
};

export default LogIgnoreFormInternal;
